import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { RecordingsService } from 'src/app/recordings/recordings.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import { FullScreenPreviewService } from 'src/app/utils/full-screen-preview/full-screen-preview.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { getCurrentEnv } from 'src/app/core/http/env-utils';

type ImgFile = File & { previewUrl?: string };

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: [
    '../../recordings/recordings/recordings.component.css',
    '../../recordings/heatmaps/heatmaps.component.css',
    './ai-chat.component.css',
  ],
})
export class SignalsAiChatComponent implements OnInit, OnDestroy {
  constructor(
    private recorderService: RecordingsService,
    private route: ActivatedRoute,
    private location: Location,
    public dialog: MatDialog,
    private router: Router,
    private fullScreenPreviewService: FullScreenPreviewService,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
  ) {}
  private subs: Subscription = new Subscription();
  private askAISub: Subscription = new Subscription();
  private getChatSub: Subscription = new Subscription();
  private currentAbort: AbortController | null = null;

  private resolveApiUrl(path: string): string {
    const selectedEnvironment = getCurrentEnv();
    const baseRaw = (selectedEnvironment as any)?.address || '';
    const base = baseRaw.replace(/\/+$/, '');
    const baseNoApi = base.replace(/\/api$/, '');
    const normalizedPath = path.startsWith('/') ? path : '/' + path;
    return `${baseNoApi}/api${normalizedPath}`;
  }

  private handleStreamPayload(
    payload: any,
    push: (chunk: string) => void,
    onStatus?: (statusKey: string) => void,
    onError?: (err: any) => void,
  ) {
    if (typeof payload === 'string') {
      if (payload === '[DONE]') return; // SSE termination
      push(payload);
      return;
    }
    // Ignore control markers (do not render them)
    if (payload && (payload.type === 'done' || payload.type === 'complete')) {
      return;
    }
    // Streamed error objects
    if (
      payload?.type === 'error' ||
      payload?.error ||
      payload?.error?.message
    ) {
      onError && onError(payload);
      return;
    }
    // Optional status updates
    if (payload?.type === 'status' && payload?.status) {
      onStatus && onStatus(payload.status);
      return;
    }
    const piece = payload?.delta ?? payload?.content ?? payload?.token ?? '';
    if (piece) push(piece);
  }

  private consumeSSE(
    buffer: string,
    onPayload: (payload: any) => void,
  ): { buffer: string; advanced: boolean } {
    let advanced = false;
    const events = buffer.split(/\r?\n\r?\n/);
    for (let i = 0; i < events.length - 1; i++) {
      const block = events[i];
      const lines = block.split(/\r?\n/).filter(Boolean);
      const dataLines = lines
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim());
      dataLines.forEach((data) => {
        advanced = true;
        if (!data) return;
        if (data === '[DONE]') {
          onPayload('[DONE]');
          return;
        }
        try {
          onPayload(JSON.parse(data));
        } catch {
          onPayload(data);
        }
      });
    }
    return { buffer: events[events.length - 1], advanced };
  }

  private consumeNDJSON(
    buffer: string,
    onPayload: (payload: any) => void,
  ): { buffer: string; advanced: boolean } {
    let advanced = false;
    const lines = buffer.split(/\r?\n/);
    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      advanced = true;
      try {
        onPayload(JSON.parse(line));
      } catch {
        onPayload(line);
      }
    }
    return { buffer: lines[lines.length - 1], advanced };
  }

  private async safeText(resp: Response): Promise<string> {
    try {
      return await resp.text();
    } catch {
      return '';
    }
  }

  private stripSources(s: string): string {
    if (!s) return '';

    return s
      .replace(/【\d+:\d+†source】/g, '')
      .replace(/【[^】]*†[^】*]*】/g, '');
  }

  private stopMessageLoaderIfAny() {
    try {
      this.messageLoaderLineIndex = 0;
      clearInterval(this.messageLoaderInterval);
    } catch {}
    this.sendMessageLoading = false;
    this.disableTextareaInput = false;
  }

  private startMessageLoaderIfAny() {
    this.disableTextareaInput = true;
    // this.sendMessageLoading = true;
    // this.messageLoaderFunc();
  }

  // Core streaming call
  private async streamAskAI(
    formData: FormData,
    aiMessageRef: {
      message: string;
      isUser: boolean;
      createdAt: string;
      images: any[];
    },
  ) {
    this.streamingStarted = true;
    // Use normalized API base from environments (handles trailing slashes and optional /api)
    const url = this.resolveApiUrl('/recorder/ask-ai?stream=true');

    // Abort previous stream if any
    this.currentAbort?.abort();
    const controller = new AbortController();
    this.currentAbort = controller;

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: 'token:' + this.localStorageService.get('auth_key'),
          Accept: 'text/event-stream',
        },
        body: formData,
        signal: controller.signal,
      });

      if (!resp.ok) {
        const text = await this.safeText(resp);
        throw new Error(`HTTP ${resp.status}: ${text || 'Request failed'}`);
      }

      // If server did NOT stream and replied with JSON, handle it like the old non-stream flow.
      const ctype = resp.headers.get('content-type') || '';
      if (
        ctype.includes('application/json') &&
        !ctype.includes('event-stream')
      ) {
        try {
          const json = await resp.json();
          // Replace the placeholder with the final answer
          aiMessageRef.message = this.stripSources(
            json?.assistantMessage?.content?.[0]?.text?.value ||
              json?.assistantMessage?.message ||
              json?.message ||
              JSON.stringify(json),
          );
          // Keep raw content up-to-date for future re-renders when assistantMessage exists
          if (json?.assistantMessage)
            this.chatContentRaw.unshift(json.assistantMessage);
        } catch {
          aiMessageRef.message = 'Sorry — something went wrong (invalid JSON).';
        }
        // Stop loaders and short-circuit streaming path
        this.stopMessageLoaderIfAny();
        return;
      }

      if (!resp.body || !('getReader' in resp.body)) {
        // Fallback – no stream support: let the existing non-stream path handle it
        throw new Error('Streaming not supported by this browser/response.');
      }

      // Begin streaming the body
      const reader = (resp.body as ReadableStream<Uint8Array>).getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      let stopStreaming = false;
      let currentStatus: string | null = null;

      const onStatus = (statusKey: string) => {
        currentStatus = statusKey;
        aiMessageRef.message = statusKey;
        this.scrollToBottom('auto');
      };

      const onError = (errPayload: any) => {
        // Extract a helpful message
        const msg =
          errPayload?.message ||
          errPayload?.error?.message ||
          errPayload?.detail ||
          errPayload?.error ||
          'Something went wrong! Please try again.';

        console.error('Stream error payload:', errPayload);
        stopStreaming = true;
        try {
          controller.abort();
        } catch {}
        currentStatus = null;
        aiMessageRef.message = String(msg);
        this.stopMessageLoaderIfAny();
      };

      const push = (chunk: string) => {
        if (stopStreaming) return;
        if (!chunk) return;
        if (currentStatus !== null) {
          aiMessageRef.message = '';
          currentStatus = null;
        }
        if (
          aiMessageRef.message === 'Thinking...' ||
          aiMessageRef.message.includes('Thinking')
        ) {
          aiMessageRef.message = '';
        }
        aiMessageRef.message = this.addStrongTagsStreamingSafe(
          this.stripSources(chunk),
        );
        this.scrollToBottom('auto');
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (stopStreaming) break;

        buffer += decoder.decode(value, { stream: true });

        let advanced = false;
        ({ buffer, advanced } = this.consumeSSE(buffer, (p) =>
          this.handleStreamPayload(p, push, onStatus, onError),
        ));
        if (!advanced) {
          ({ buffer, advanced } = this.consumeNDJSON(buffer, (p) =>
            this.handleStreamPayload(p, push, onStatus, onError),
          ));
        }
        if (!advanced && buffer.length > 2048) {
          push(buffer);
          buffer = '';
        }
      }

      // Guard: if streaming was stopped due to error, skip final parsing
      if (stopStreaming) {
        buffer = '';
        return;
      }

      // Final drain: parse any remaining SSE/NDJSON blocks.
      let finalAdvanced = false;
      ({ buffer, advanced: finalAdvanced } = this.consumeSSE(buffer, (p) =>
        this.handleStreamPayload(p, push, onStatus, onError),
      ));
      if (!finalAdvanced) {
        ({ buffer, advanced: finalAdvanced } = this.consumeNDJSON(buffer, (p) =>
          this.handleStreamPayload(p, push, onStatus, onError),
        ));
      }
      const rest = buffer.trim();
      // Only push if it's plain text (avoid JSON control like {type:"complete"})
      if (
        !finalAdvanced &&
        rest &&
        !rest.startsWith('{') &&
        !rest.startsWith('[')
      ) {
        push(rest);
      }
      buffer = '';
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // user cancelled; keep partial text
        if (aiMessageRef.message === 'Thinking...') {
          aiMessageRef.message = 'Stopped searching';
        } else if (aiMessageRef.message === 'Thinking...') {
          aiMessageRef.message += '..';
        }
        console.warn('Stream aborted by user.');
      } else {
        console.error(err);
        if (aiMessageRef.message === 'Thinking...' || !aiMessageRef.message) {
          aiMessageRef.message =
            'Sorry — something went wrong while streaming.';
        }
      }
    } finally {
      // Flush any remaining streaming-buffered markdown into the final message
      try {
        if (
          this.streamingMarkdownBuffer &&
          aiMessageRef &&
          aiMessageRef.message != null
        ) {
          // Ensure final render is consistent (no duplication)
          aiMessageRef.message = this.addStrongTags(
            this.stripSources(this.streamingMarkdownBuffer),
          );
        }
      } catch (e) {}
      this.streamingMarkdownBuffer = '';

      this.streamingStarted = false;
      this.currentAbort = null;
      this.stopMessageLoaderIfAny();

      // If this was a comparison chat and it was initializing/pending, refresh analyses list minimally
      if (
        this.selectedChatObj?.comparison &&
        (this.selectedChatObj?.status === 'Initialized' ||
          this.selectedChatObj?.status === 'Pending')
      ) {
        this.getAIChatHistory(true);
      }
    }
  }

  public cancelCurrentAI() {
    this.currentAbort?.abort();
  }

  confirmationDialogRef: MatDialogRef<ConfirmationComponent>;

  streamingStarted: boolean = false;
  firstLoader: boolean = true;
  isLoading: boolean = false;
  isChatLoading: boolean = false;
  analysesHistory: any = [];
  totalItems: number = 0;

  selectedChatObj: any = {};
  target: string = '';
  chatID: string = '';

  chatContentRaw: any = [];
  chatContent: any = [];
  chatInput: string = '';
  constructedChatContent: any = [];

  darkMode: boolean = false;

  params_constructed: any = {};
  showPlaceholderWithFilters: boolean = false;

  sendMessageLoading: boolean = false;

  messageLoaderInterval: any;
  messageLoaderLineIndex: number = 0;
  loaderMessages = [
    'Processing your request…',
    'Preparing your analysis…',
    'Generating your response…',
    'Organizing your results…',
    'Working on your prompt…',
    'Gathering insights…',
    'Reviewing your input…',
    'Analyzing data…',
    'Collecting information…',
    'Structuring your output…',
    'Crafting your response…',
    'Compiling relevant details…',
    'Checking available data…',
    'Finding relevant information…',
    'Refining your request…',
    'Aligning your data…',
    'Assembling your response…',
    'Summarizing key points…',
    'Loading your data…',
    'Reviewing details…',
    'Processing input…',
    'Organizing content…',
    'Generating insights…',
    'Formulating your output…',
    'Thinking through your request…',
    'Connecting the dots…',
    'Clarifying context…',
    'Preparing details…',
    'Arranging information…',
    'Polishing your response…',
    'Working on results…',
    'Gathering key data…',
    'Reviewing context…',
    'Analyzing your input…',
    'Retrieving information…',
    'Summarizing insights…',
    'Refining output…',
    'Wrapping up your response…',
    'Almost ready…',
    'Finalizing details…',
  ];
  disableTextareaInput: boolean = false;

  comparisonSummaries: any = null;
  commonFilters: any = [];

  recordingParams: any = {};

  ngOnInit(): void {
    if (this.localStorageService.get('darkMode') === true) {
      document.body.classList.add('dark-theme');
      this.darkMode = true;
    } else if (this.localStorageService.get('darkMode') === false) {
      document.body.classList.remove('dark-theme');
      this.darkMode = false;
    } else {
      const deviceMode = window.matchMedia('(prefers-color-scheme: dark)');
      if (deviceMode.matches) {
        document.body.classList.add('dark-theme');
        this.localStorageService.set('darkMode', true);
        this.darkMode = true;
      }
    }

    this.recordingParams = JSON.parse(
      sessionStorage.getItem('recordingParamObj_signals') || '{}',
    );

    this.constructRecordingParamObj();

    this.route.params.subscribe((params) => {
      this.target = params['chatTarget'];
      this.chatID = params['id'];

      this.getAIChatHistory();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.askAISub.unsubscribe();
    this.getChatSub.unsubscribe();
    if (this.getChatTimeout) clearTimeout(this.getChatTimeout);
    sessionStorage.removeItem('recordingParamObj_signals');
  }

  formatDate(date: string) {
    return new Date(date).toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      day: 'numeric',
      month: 'short',
    });
  }

  constructRecordingParamObj(includeFilters: boolean = true) {
    try {
      this.params_constructed = {
        startDate: this.formatDate(
          this.selectedChatObj?.params?.startDate?.split('@')[0],
        ),
        endDate: this.formatDate(
          this.selectedChatObj?.params?.endDate?.split('@')[0],
        ),
        filters: includeFilters ? this.getActiveFilters() : [],
        userId: this.selectedChatObj?.params?.userId || false,
      };
      this.showPlaceholderWithFilters = true;
    } catch (e) {
      this.showPlaceholderWithFilters = false;
      console.log(e);
    }
  }

  truncateText(
    text: string | null | undefined,
    maxLength: number = 50,
  ): string {
    if (!text) return '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  }

  constructAIChatHistory(data: any, refetchAnalysesOnly: boolean = false) {
    this.isLoading = false;
    this.firstLoader = false;

    if (!data || data.length === 0) {
      if (refetchAnalysesOnly) return;

      this.analysesHistory = [];
      this.selectedChatObj = {};
      this.comparisonSummaries = {
        groupA: [],
        groupB: [],
      };
      return;
    }

    this.analysesHistory = data;
    // this.totalItems = data.length;

    try {
      this.analysesHistory.forEach((analysis: any) => {
        analysis.updatedAt = this.formatDate(analysis.updatedAt);
      });
    } catch (err) {
      this.notificationService.error('Something went wrong!');
      console.log(err);
    }

    this.selectedChatObj = this.analysesHistory.find(
      (analysis: any) => analysis.videoID === this.chatID,
    );

    if (this.selectedChatObj?.comparison) {
      try {
        if (this.params_constructed) this.params_constructed.filters = [];
      } catch (e) {
        console.log(e);
      }
      // this.commonFilters =
      //   this.returnCommonAndDifferentFilters()?.commonFilters;
      const groupAFilters = [
        ...this.returnCommonAndDifferentFilters()?.commonFilters,
        ...this.returnCommonAndDifferentFilters()?.groupAFilters,
      ];
      const groupBFilters = [
        ...this.returnCommonAndDifferentFilters()?.commonFilters,
        ...this.returnCommonAndDifferentFilters()?.groupBFilters,
      ];

      this.showPlaceholderWithFilters = true;

      this.comparisonSummaries = {
        groupA: Object.entries(this.selectedChatObj?.summary?.['A'] || {}),
        groupAFilters: groupAFilters,
        groupB: Object.entries(this.selectedChatObj?.summary?.['B'] || {}),
        groupBFilters: groupBFilters,
      };
    } else {
      this.commonFilters = [];
      this.comparisonSummaries = {
        groupA: [],
        groupAFilters: [],
        groupB: [],
        groupBFilters: [],
      };
    }

    if (refetchAnalysesOnly) return;

    this.constructRecordingParamObj(!this.selectedChatObj?.comparison);

    this.getAIChat();
  }

  getAIChatHistory(refetchAnalysesOnly: boolean = false) {
    this.isLoading = true;

    this.subs.add(
      this.recorderService
        .getAIChatHistory({
          ...this.recordingParams,
          target: this.target,
          signals: true,
        })
        .subscribe({
          next: (res) => {
            if (!res || res?.length === 0) {
              this.goBackToDashboard();
              return;
            }
            this.constructAIChatHistory(res, refetchAnalysesOnly);
          },
          error: (err) => {
            this.goBackToDashboard();
            this.notificationService.error('Something went wrong!');
            console.log(err);
          },
        }),
    );
  }

  private addStrongTags(text: string): string {
    if (!text) return '';

    let result = text;

    // Headings h1 – h6
    result = result.replace(/^\s*######\s+(.*)$/gm, '<h6>$1</h6>');
    result = result.replace(/^\s*#####\s+(.*)$/gm, '<h5>$1</h5>');
    result = result.replace(/^\s*####\s+(.*)$/gm, '<h4>$1</h4>');
    result = result.replace(/^\s*###\s+(.*)$/gm, '<h3>$1</h3>');
    result = result.replace(/^\s*##\s+(.*)$/gm, '<h2>$1</h2>');
    result = result.replace(/^\s*#\s+(.*)$/gm, '<h1>$1</h1>');

    // Bold
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic (optional)
    result = result.replace(/\*(.*?)\*/g, '<em>$1</em>');

    return result;
  }

  private streamingMarkdownBuffer: string = '';
  private addStrongTagsStreamingSafe(rawChunk: string): string {
    try {
      const incoming = rawChunk || '';

      // Accumulate the full raw markdown stream
      this.streamingMarkdownBuffer =
        (this.streamingMarkdownBuffer || '') + incoming;

      // Re-render the entire formatted message on each chunk
      const processedAll = this.addStrongTags(
        this.stripSources(this.streamingMarkdownBuffer),
      );

      return processedAll;
    } catch (err) {
      console.error('addStrongTagsStreamingSafe error:', err);
      const fallback = (this.streamingMarkdownBuffer || '') + (rawChunk || '');
      this.streamingMarkdownBuffer = '';
      return this.addStrongTags(this.stripSources(fallback));
    }
  }

  constructAIChat() {
    this.isChatLoading = false;
    this.constructedChatContent = [];

    if (
      !this.chatContentRaw ||
      this.chatContentRaw.length === 0 ||
      typeof this.chatContentRaw === 'string'
    ) {
      return;
    }

    this.chatContentRaw.forEach((message: any) => {
      let messageText =
        message.content.filter((message: any) => {
          return message.type === 'text';
        })?.[0]?.text?.value || '';

      messageText = messageText?.replace(/【\d+:\d+†source】/g, '');

      let parsedMessageText = this.addStrongTags(messageText);

      const images =
        message.content
          .filter((message: any) => {
            return message.type === 'image_url';
          })
          ?.map((message: any) => {
            return message.image_url;
          }) || [];

      this.constructedChatContent.push({
        message: this.stripSources(parsedMessageText),
        isUser: message.role === 'user',
        createdAt: this.formatDate(message.created_at),
        images: images,
      });
    });

    this.constructedChatContent.reverse();
  }

  getChatTimeout: any = null;
  timeoutTime: number = 2000;
  intervalChatID: string = '';

  getAIChat(behavior: ScrollBehavior = 'smooth', showLoading: boolean = true) {
    this.isChatLoading = showLoading;
    this.chatContentRaw = [];
    this.constructedChatContent = [];
    this.askAISub?.unsubscribe();
    this.getChatSub?.unsubscribe();
    this.getChatSub = this.recorderService
      .getAIChat({ target: this.target, id: this.chatID, signals: true })
      .subscribe({
        next: (res) => {
          if (!res) {
            this.chatContentRaw = [];
            this.constructAIChat();
            this.notificationService.error('Something went wrong!');
            return;
          }

          if (res.status === 'Initialized') {
            this.isChatLoading = false;
            this.intervalChatID = '';
            if (this.getChatTimeout) clearTimeout(this.getChatTimeout);

            this.sendMessage(true);
          } else if (res.status === 'Pending') {
            if (!this.sendMessageLoading) {
              this.sendMessageLoading = true;
              this.messageLoaderFunc();
            }

            this.intervalChatID = this.chatID;
            this.getChatTimeout = setTimeout(() => {
              this.getAIChat('smooth', false);
            }, this.timeoutTime);
          } else {
            this.intervalChatID = '';
            if (this.getChatTimeout) clearTimeout(this.getChatTimeout);

            try {
              this.askAISub.unsubscribe();
              this.sendMessageLoading = false;
              this.messageLoaderLineIndex = 0;
              clearInterval(this.messageLoaderInterval);
            } catch (e) {}

            if (
              this.selectedChatObj?.comparison &&
              (this.selectedChatObj?.status == 'Initialized' ||
                this.selectedChatObj?.status == 'Pending')
            ) {
              this.getAIChatHistory(true);
            }

            this.chatContentRaw = res;
            this.constructAIChat();
            this.scrollToBottom(behavior);
          }
        },
        error: (err) => {
          this.chatContentRaw = [];
          this.constructAIChat();
          this.notificationService.error('Something went wrong!');
          console.log(err);
        },
      });
  }

  goBackToDashboard() {
    this.router.navigate(['/signals']);
  }

  excludeKeys = [
    'target',
    'error',
    'showAll',
    'byDate',
    'device',
    'exactMatch',
  ];
  keysWithoutValue = [
    'directTraffic',
    'newVisit',
    'returningUsers',
    'rageClicks',
  ];
  returnCommonAndDifferentFilters(): {
    commonFilters: any[];
    groupAFilters: any[];
    groupBFilters: any[];
  } {
    const constructValue = (key: any, value: any) => {
      try {
        if (key === 'startDate' || key === 'endDate') {
          return this.formatDate(value?.split('@')?.[0]);
        } else if (key === 'country') {
          const displayNames = new Intl.DisplayNames(['en'], {
            type: 'region',
          });
          const countryName = displayNames.of(value);
          return (
            countryName || this.selectedChatObj?.params?.country || 'Unknown'
          );
        } else if (
          key === 'desktopUsers' ||
          key === 'mobileUsers' ||
          key === 'tabletUsers'
        ) {
          switch (key) {
            case 'desktopUsers':
              return 'Desktop';
            case 'mobileUsers':
              return 'Mobile';
            case 'tabletUsers':
              return 'Tablet';
          }
        } else if (key == 'locations' && value == 'all_pages') {
          return 'All Pages';
        } else if (key == 'durationRange') {
          const [min, max] = value.split(',');
          let tmpValue = '';

          if (min && max) {
            tmpValue = `${min} to ${max} minutes`;
          } else if (min) {
            tmpValue = `Min ${min} minutes`;
          } else if (max) {
            tmpValue = `Max ${max} minutes`;
          }
          return tmpValue;
        } else if (key == 'pageRange') {
          const [min, max] = value.split(',');
          let tmpValue = '';

          if (min && max) {
            tmpValue = `${min} to ${max}`;
          } else if (min) {
            tmpValue = `Min ${min}`;
          } else if (max) {
            tmpValue = `Max ${max}`;
          }
          return tmpValue;
        } else if (key == 'actionRange') {
          const [min, max] = value.split(',');
          let tmpValue = '';

          if (min && max) {
            tmpValue = `${min} to ${max}`;
          } else if (min) {
            tmpValue = `Min ${min}`;
          } else if (max) {
            tmpValue = `Max ${max}`;
          }
          return tmpValue;
        } else if (key == 'rageClicks') {
          return 'Rage Clicks';
        } else if (key == 'newVisit') {
          return 'New';
        } else if (key == 'returningUsers') {
          return 'Returning';
        } else if (key == 'directTraffic') {
          return 'Direct';
        }
        return value;
      } catch (error) {
        console.log(error);
        return value;
      }
    };
    const constructLabel = (key: any, value?: any) => {
      try {
        const mapping: any = {
          startDate: 'Start Date',
          endDate: 'End Date',
          country: 'Country',
          device: 'Device',
          byDate: 'By Date',
          desktopUsers: 'Platform',
          mobileUsers: 'Platform',
          tabletUsers: 'Platform',
          locations: value == 'all_pages' ? 'Location' : 'Locations',
          os: 'OS',
          browser: 'Browser',
          deviceType: 'Device',
          durationRange: 'Duration',
          pageRange: 'Pages',
          actionRange: 'Actions',
          directTraffic: 'Traffic Source',
          newVisit: 'Visitor Type',
          returningUsers: 'Visitor Type',
          rageClicks: 'Behavior',
          nudges: 'Nudges',
        };

        return mapping[key];
      } catch (error) {
        console.log(error);
        return key;
      }
    };

    const commonFilters: any = [];
    const groupAFilters: any = [];
    const groupBFilters: any = [];

    const params = this.selectedChatObj.params;
    const comparisonParams = this.selectedChatObj.comparisonParams;
    const groupAFilterKeys = Object.keys(params).filter(
      (key) => !this.excludeKeys.includes(key),
    );
    const groupBFilterKeys = Object.keys(comparisonParams);

    for (let i = 0; i < groupAFilterKeys.length; i++) {
      if (
        !groupBFilterKeys[i] ||
        !groupBFilterKeys.includes(groupAFilterKeys[i])
      ) {
        groupAFilters.push({
          label: constructLabel(
            groupAFilterKeys[i],
            params[groupAFilterKeys[i]],
          ),
          value: constructValue(
            groupAFilterKeys[i],
            params[groupAFilterKeys[i]].toString(),
          ),
        });
        continue;
      }

      if (groupBFilterKeys.includes(groupAFilterKeys[i])) {
        const groupAValue = params[groupAFilterKeys[i]].toString();
        const groupBValue = comparisonParams[groupBFilterKeys[i]].toString();

        if (groupAValue == groupBValue) {
          commonFilters.push({
            value: constructValue(
              groupAFilterKeys[i],
              params[groupAFilterKeys[i]].toString(),
            ),
            label: constructLabel(groupAFilterKeys[i]),
          });
        } else {
          groupAFilters.push({
            value: constructValue(
              groupAFilterKeys[i],
              params[groupAFilterKeys[i]].toString(),
            ),
            label: constructLabel(groupAFilterKeys[i]),
          });
          groupBFilters.push({
            value: constructValue(
              groupBFilterKeys[i],
              comparisonParams[groupBFilterKeys[i]].toString(),
            ),
            label: constructLabel(groupBFilterKeys[i]),
          });
        }
      }
    }

    for (let i = 0; i < groupBFilterKeys.length; i++) {
      if (
        !groupAFilterKeys.includes(groupBFilterKeys[i]) &&
        !groupBFilters
          .map((filter: any) => filter.label)
          .includes(constructLabel(groupBFilterKeys[i]))
      ) {
        groupBFilters.push({
          value: constructValue(
            groupBFilterKeys[i],
            comparisonParams[groupBFilterKeys[i]].toString(),
          ),
          label: constructLabel(groupBFilterKeys[i]),
        });
      }
    }

    return {
      commonFilters,
      groupAFilters,
      groupBFilters,
    };
  }
  selectAnalysis(analysis: any) {
    if (this.chatID === analysis.videoID) {
      return;
    }

    if (this.getChatTimeout) {
      this.intervalChatID = '';
      clearTimeout(this.getChatTimeout);
    }

    const currentPath = this.location.path();

    const [pathPart, queryString] = currentPath.split('?');

    const segments = pathPart.split('/');
    segments[segments.length - 1] = analysis.videoID;

    const updatedPath = segments.join('/');

    const updatedURL = queryString
      ? `${updatedPath}?${queryString}`
      : updatedPath;

    this.location.replaceState(updatedURL);

    this.chatID = analysis.videoID;

    this.selectedChatObj = analysis;

    if (this.selectedChatObj?.comparison) {
      try {
        if (this.params_constructed) this.params_constructed.filters = [];
      } catch (e) {
        console.log(e);
      }

      const groupAFilters = [
        ...this.returnCommonAndDifferentFilters()?.commonFilters,
        ...this.returnCommonAndDifferentFilters()?.groupAFilters,
      ];
      const groupBFilters = [
        ...this.returnCommonAndDifferentFilters()?.commonFilters,
        ...this.returnCommonAndDifferentFilters()?.groupBFilters,
      ];

      this.showPlaceholderWithFilters = true;
      // this.commonFilters =
      //   this.returnCommonAndDifferentFilters()?.commonFilters;
      this.comparisonSummaries = {
        groupA: Object.entries(this.selectedChatObj?.summary?.['A'] || {}),
        groupAFilters: groupAFilters,
        groupB: Object.entries(this.selectedChatObj?.summary?.['B'] || {}),
        groupBFilters: groupBFilters,
      };
    } else {
      this.commonFilters = [];
      this.comparisonSummaries = {
        groupA: [],
        groupAFilters: [],
        groupB: [],
        groupBFilters: [],
      };
    }
    this.constructRecordingParamObj(this.selectedChatObj?.comparison);

    this.sendMessageLoading = false;

    this.getAIChat('auto');
  }

  onEnter(event: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.sendMessage();
    }
  }

  messageLoaderFunc() {
    this.messageLoaderInterval = setInterval(() => {
      if (this.messageLoaderLineIndex + 1 == this.loaderMessages.length) {
        clearInterval(this.messageLoaderInterval);
      } else {
        this.messageLoaderLineIndex++;
      }
    }, 6000);
  }

  sendMessage(bypassChatInput: boolean = false) {
    if (!bypassChatInput) {
      if (!this.chatInput || this.chatInput.length === 0) {
        return;
      }

      const userMessage = {
        role: 'user',
        content: [
          {
            type: 'text',
            text: {
              value: this.chatInput?.trim(),
            },
          },
          ...this.imagesToSend.map((file: any) => {
            return {
              type: 'image_url',
              image_url: {
                url: file.previewUrl,
              },
            };
          }),
        ],
        created_at: new Date().toISOString(),
      };

      this.constructedChatContent.push({
        message: this.chatInput?.trim(),
        isUser: true,
        createdAt: this.formatDate(new Date().toISOString()),
        images: [
          ...this.imagesToSend.map((file: any) => {
            return {
              type: 'image_url',
              url: file.previewUrl,
            };
          }),
        ],
      });

      this.chatContentRaw.unshift(userMessage);

      this.scrollToBottom();
    }

    // Prepare a streaming AI placeholder message in the UI
    const aiMessage = {
      message: 'Thinking...',
      isUser: false,
      createdAt: this.formatDate(new Date().toISOString()),
      images: [],
    };
    this.constructedChatContent.push(aiMessage);

    const params: any = {
      conversationId: this.chatID,
      prompt: this.chatInput,
      target: this.selectedChatObj?.params?.target || this.target,

      params: {
        ...this.selectedChatObj?.params,
      },

      // comparison params
      ...(this.selectedChatObj?.comparison && {
        comparisonParams: this.selectedChatObj?.comparisonParams,
      }),
    };

    const formData = this.buildAskAIFormData(params, this.imagesToSend);
    formData.append('signals', 'true');

    this.chatInput = '';
    this.clearImages();

    // Start optional loader text cycling (kept from existing UX)
    this.startMessageLoaderIfAny();

    // Try streaming first; if it fails for any reason, fall back to the existing non-stream endpoint
    (async () => {
      try {
        await this.streamAskAI(formData, aiMessage);
      } catch (e) {
        // Fallback to the existing non-stream request
        try {
          this.askAISub?.unsubscribe();
        } catch {}

        this.askAISub = this.recorderService.askAI(formData).subscribe({
          next: (res) => {
            this.stopMessageLoaderIfAny();

            if (
              this.selectedChatObj?.comparison &&
              (this.selectedChatObj?.status == 'Initialized' ||
                this.selectedChatObj?.status == 'Pending')
            ) {
              this.getAIChatHistory(true);
            }

            // Replace the placeholder with the final answer
            aiMessage.message = this.stripSources(
              res?.assistantMessage?.content?.[0]?.text?.value ||
                res?.assistantMessage?.message ||
                JSON.stringify(res?.assistantMessage || res || ''),
            );

            // Keep raw content up-to-date for future re-renders
            this.chatContentRaw.unshift(res?.assistantMessage);
            this.constructAIChat();
          },
          error: (err) => {
            this.stopMessageLoaderIfAny();
            console.log(err);
            aiMessage.message = 'Sorry — something went wrong.';
          },
        });
      }
    })();
  }

  adjustTextareaHeight(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto'; // reset to shrink if needed

    const maxHeight = 300; // adjust as you like

    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
  }

  toogleOpenClose(event: any) {
    event.target.closest('.heatmap-side-content').classList.toggle('close');
  }

  sidenavToogle: boolean = false;
  triggerSidenav() {
    if (window.innerWidth > 1200) {
      this.sidenavToogle = false;
      return;
    }
    this.sidenavToogle = !this.sidenavToogle;
  }

  getActiveFilters(): Array<{ type: string; label: string; value: string }> {
    const filters: Array<{ type: string; label: string; value: string }> = [];

    if (this.selectedChatObj?.params?.country) {
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const countryName = displayNames.of(
          this.selectedChatObj?.params?.country,
        );
        filters.push({
          type: 'country',
          label: 'Country',
          value:
            countryName || this.selectedChatObj?.params?.country || 'Unknown',
        });
      } catch (e) {
        filters.push({
          type: 'country',
          label: 'Country',
          value: this.selectedChatObj?.params?.country,
        });
      }
    }

    if (this.selectedChatObj?.params?.browser) {
      filters.push({
        type: 'browser',
        label: 'Browser',
        value: this.selectedChatObj?.params?.browser,
      });
    }

    // if (this.selectedChatObj?.params?.directTraffic) {
    //   filters.push({
    //     type: 'directTraffic',
    //     label: '',
    //     value: 'Direct Traffic',
    //   });
    // }
    // if (this.selectedChatObj?.params?.newVisit) {
    //   filters.push({
    //     type: 'newVisit',
    //     label: '',
    //     value: 'New Visit',
    //   });
    // }
    // if (this.selectedChatObj?.params?.desktopUsers) {
    //   filters.push({
    //     type: 'desktopUsers',
    //     label: '',
    //     value: 'Desktop Users',
    //   });
    // }
    // if (this.selectedChatObj?.params?.tabletUsers) {
    //   filters.push({
    //     type: 'tabletUsers',
    //     label: '',
    //     value: 'Tablet Users',
    //   });
    // }
    // if (this.selectedChatObj?.params?.mobileUsers) {
    //   filters.push({
    //     type: 'mobileUsers',
    //     label: '',
    //     value: 'Mobile Users',
    //   });
    // }
    // if (this.selectedChatObj?.params?.rageClicks) {
    //   filters.push({
    //     type: 'rageClicks',
    //     label: '',
    //     value: 'Rage Clicks',
    //   });
    // }
    // if (this.selectedChatObj?.params?.returningUsers) {
    //   filters.push({
    //     type: 'returningUsers',
    //     label: '',
    //     value: 'Returning Users',
    //   });
    // }

    if (this.selectedChatObj?.params?.nudge) {
      let value = '';

      const nudges = this.selectedChatObj?.params?.nudge?.split(',');

      if (nudges?.length > 1) {
        value = `${nudges?.length} nudges`;
      } else {
        value = String(nudges);
      }

      filters.push({
        type: 'nudge',
        label: 'Nudge',
        value: value,
      });
    }

    if (this.selectedChatObj?.params?.os) {
      filters.push({
        type: 'os',
        label: 'OS',
        value: this.selectedChatObj?.params?.os,
      });
    }

    if (this.selectedChatObj?.params?.locations?.length) {
      let label = this.selectedChatObj?.params?.exactMatch
        ? 'Exact URL'
        : 'URL Contains';
      let value =
        this.selectedChatObj?.params?.locations.length === 1
          ? this.selectedChatObj?.params?.locations[0]
          : `${this.selectedChatObj?.params?.locations.length} locations`;

      if (value == 'all_pages') {
        label = 'Location';
        value = 'All Pages';
      }

      filters.push({
        type: 'visitedURLs',
        label,
        value,
      });
    }

    if (this.selectedChatObj?.params?.referrer?.length) {
      let label = 'Referrer';
      let value =
        this.selectedChatObj?.params?.referrer.length === 1
          ? this.selectedChatObj?.params?.referrer[0]
          : `${this.selectedChatObj?.params?.referrer.length} referrers`;

      filters.push({
        type: 'referrer',
        label,
        value,
      });
    }

    if (this.selectedChatObj?.params?.durationRange) {
      const [min, max] = this.selectedChatObj?.params?.durationRange.split(',');
      let value = '';

      if (min && max) {
        value = `${min} to ${max} minutes`;
      } else if (min) {
        value = `Min ${min} minutes`;
      } else if (max) {
        value = `Max ${max} minutes`;
      }

      filters.push({
        type: 'duration',
        label: 'Duration',
        value,
      });
    }

    if (this.selectedChatObj?.params?.pageRange) {
      const [min, max] = this.selectedChatObj?.params?.pageRange.split(',');
      let value = '';

      if (min && max) {
        value = `${min} to ${max}`;
      } else if (min) {
        value = `Min ${min}`;
      } else if (max) {
        value = `Max ${max}`;
      }

      filters.push({
        type: 'pages',
        label: 'Pages',
        value,
      });
    }

    if (this.selectedChatObj?.params?.actionRange) {
      const [min, max] = this.selectedChatObj?.params?.actionRange.split(',');
      let value = '';

      if (min && max) {
        value = `${min} to ${max}`;
      } else if (min) {
        value = `Min ${min}`;
      } else if (max) {
        value = `Max ${max}`;
      }

      filters.push({
        type: 'actions',
        label: 'Actions',
        value,
      });
    }

    if (this.selectedChatObj?.params?.error) {
      let value = '';
      if (this.selectedChatObj?.params?.error.includes('image_error')) {
        value = 'Images failed to load';
      } else {
        value = this.selectedChatObj?.params?.error;
      }

      filters.push({
        type: 'error',
        label: 'Error',
        value: this.truncateText(value, 20),
      });
    }

    return filters.filter(
      (filter) =>
        filter &&
        filter.value != null &&
        filter.type != null &&
        typeof filter.value === 'string',
    );
  }

  scrollToBottom(behavior: ScrollBehavior = 'smooth') {
    // scroll to bottom
    setTimeout(() => {
      const chatMessages = document.querySelector('.chat-messages');
      if (chatMessages) {
        chatMessages.scrollTo({
          top: chatMessages.scrollHeight,
          behavior: behavior,
        });
      }
    }, 50);
  }

  deleteChat(chat: any) {
    this.confirmationDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: {
        header: 'Delete Chat',
        content: 'Are you sure you want to delete this chat?',
      },
    });

    this.confirmationDialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;

      this.recorderService
        .deleteAIChat({
          conversationId: chat.videoID,
          target: this.target,
          signals: true,
        })
        .subscribe({
          next: (res) => {
            if (chat.videoID === this.chatID) {
              this.chatID = this.analysesHistory?.find(
                (analysis: any) => analysis.videoID !== chat.videoID,
              )?.videoID;
              this.getAIChat();
            }
            this.getAIChatHistory();
          },
          error: (err) => {
            this.notificationService.error('Something went wrong!');
            console.log(err);
          },
        });
    });
  }

  switchThemeMode(event: any) {
    this.darkMode = event.checked;
    if (event.checked) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
    this.localStorageService.set('darkMode', this.darkMode);
  }

  // images features - start
  readonly MAX_FILES = 10;
  readonly MAX_SIZE_MB = 15;

  dragOver = false;
  files: ImgFile[] = [];
  errors: string[] = [];

  /** expose current value to parent if you want to read directly */
  get value(): File[] {
    return this.files.slice();
  }

  /** call this from parent after successful send */
  clearImages() {
    // this.files.forEach(
    //   (f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl!)
    // );
    this.files = [];
    this.errors = [];
    this.imagesToSend = [];
  }

  // ——— input[type=file] picker ———
  onPickFiles(ev: Event) {
    const input = ev.target as HTMLInputElement;
    if (input.files?.length) this.addFiles(input.files);
    input.value = ''; // allow re-selecting same file
  }

  // ——— drag & drop ———
  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.dragOver = true;
  }
  onDragLeave() {
    this.dragOver = false;
  }
  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.dragOver = false;
    if (ev.dataTransfer?.files?.length) this.addFiles(ev.dataTransfer.files);
  }

  // ——— paste screenshots/images ———
  @HostListener('document:paste', ['$event'])
  onPaste(ev: ClipboardEvent) {
    const items = ev.clipboardData?.items;
    if (!items) return;
    const pasted: File[] = [];
    for (const it of items as any) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) pasted.push(f);
      }
    }
    if (pasted.length) this.addFiles(pasted as unknown as FileList);
  }

  removeAt(index: number) {
    const [removed] = this.files.splice(index, 1);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);

    this.imagesToSend = this.files.slice();
  }

  // ——— core add/validate ———
  private addFiles(list: FileList | File[]) {
    const errs: string[] = [];

    for (const raw of Array.from(list)) {
      if (this.files.length >= this.MAX_FILES) {
        errs.push(`Maximum ${this.MAX_FILES} images allowed.`);
        break;
      }

      const f = raw as ImgFile;

      if (!f.type.startsWith('image/')) {
        errs.push(`${f.name || 'file'}: not an image.`);
        continue;
      }
      if (f.size > this.MAX_SIZE_MB * 1024 * 1024) {
        errs.push(`${f.name || 'image'}: > ${this.MAX_SIZE_MB}MB.`);
        continue;
      }
      // simple de-dupe: name + size
      if (this.files.some((x) => x.name === f.name && x.size === f.size))
        continue;

      f.previewUrl = URL.createObjectURL(f);
      this.files.push(f);
    }

    this.errors = errs;

    this.imagesToSend = this.files.slice();
  }

  // in your existing chat page component .ts
  imagesToSend: File[] = [];

  async returnFormData() {
    // your existing message sending logic...
    const prompt = this.chatInput?.trim();
    if (!prompt && this.imagesToSend.length === 0) return;

    const fd = new FormData();
    fd.append('prompt', prompt || '');
    this.imagesToSend.forEach((f, i) =>
      fd.append('images', f, f.name || `image-${i}.png`),
    );

    // await this.api.post(fd).toPromise();

    return fd;
  }

  // Build ONE FormData with JSON + files
  private buildAskAIFormData(params: any, images: File[]): FormData {
    const fd = new FormData();

    // put params as JSON blob/string
    fd.append('conversationId', params.conversationId);
    fd.append('target', params.target);

    fd.append('prompt', params.prompt);

    const fieldNames = [
      'photo1',
      'photo2',
      'photo3',
      'photo4',
      'photo5',
      'photo6',
      'photo7',
      'photo8',
      'photo9',
      'photo10',
    ];
    images.slice(0, fieldNames.length).forEach((file, i) => {
      fd.append(
        file.name || fieldNames[i],
        file,
        file.name || `image-${i}.png`,
      );
    });

    return fd;
  }

  public showScreenshot(img: any): void {
    if (
      img.includes(
        'https://behamicsfront.blob.core.windows.net/automationimages/whiteimages/',
      )
    ) {
      return;
    }

    const content = {
      src: img,
      html: '',
      label: '',
      isHTML: false,
      showLabel: false,
    };

    this.fullScreenPreviewService.setContent(content);
    this.fullScreenPreviewService.openPreview();
  }

  // images features - end

  public checkIfTooltipNeedsToBeShown(session: any, index: number): void {
    try {
      const DOM_element = document.querySelectorAll(
        '.screen-recorder-heatmaps_list-item',
      )?.[index];

      if (DOM_element) {
        const p_element = DOM_element.querySelector('p');

        if (p_element && p_element.clientWidth < p_element.scrollWidth) {
          this.analysesHistory[index].tooltipText = session.title;
        } else {
          this.analysesHistory[index].tooltipText = '';
        }
      }
    } catch (e) {
      console.log(e);
    }
  }
}
